import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Build a compact list of page numbers to render.
 * For small ranges (<= 7) we show everything; otherwise we
 * collapse the middle into an ellipsis to keep the bar tidy.
 */
function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => pages.add(n));
  if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((n) => pages.add(n));
  const sorted = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("...");
    out.push(sorted[i]);
  }
  return out;
}

export default function Pagination({ currentPage, totalPages, onPageChange }) {
  if (!totalPages || totalPages <= 1) return null;

  const pages = buildPageList(currentPage, totalPages);
  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= totalPages;

  const baseBtn =
    "min-w-9 h-9 px-3 inline-flex items-center justify-center rounded-md text-sm border transition select-none";
  const idle =
    "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10";
  const active =
    "bg-rose-600 border-rose-500 text-white shadow-[0_0_14px_rgba(244,63,94,0.35)]";
  const disabled =
    "bg-white/5 border-white/5 text-zinc-600 cursor-not-allowed";

  return (
    <nav
      className="mt-10 flex items-center justify-center gap-1.5 flex-wrap"
      role="navigation"
      aria-label="Pagination"
      data-testid="posts-pagination"
    >
      <button
        type="button"
        onClick={() => !prevDisabled && onPageChange(currentPage - 1)}
        disabled={prevDisabled}
        aria-label="Previous page"
        data-testid="pagination-prev"
        className={`${baseBtn} ${prevDisabled ? disabled : idle}`}
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        <span>Previous</span>
      </button>

      {pages.map((p, idx) =>
        p === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            className="min-w-9 h-9 inline-flex items-center justify-center text-zinc-500 text-sm"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-current={p === currentPage ? "page" : undefined}
            data-testid={`pagination-page-${p}`}
            className={`${baseBtn} ${p === currentPage ? active : idle}`}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => !nextDisabled && onPageChange(currentPage + 1)}
        disabled={nextDisabled}
        aria-label="Next page"
        data-testid="pagination-next"
        className={`${baseBtn} ${nextDisabled ? disabled : idle}`}
      >
        <span>Next</span>
        <ChevronRight className="w-4 h-4 ml-1" />
      </button>
    </nav>
  );
}
